import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  security,
  type FixUnit,
  type SecurityHostSummary,
  type VulnFinding,
  type VulnSeverity,
} from "../../lib/api";
import { useUI } from "../../lib/store";
import { cn } from "../../lib/cn";
import { relativeShort } from "../../lib/format";
import { Button } from "../shared/Button";
import { ChevronRight } from "../shared/Icon";
import { Skeleton } from "../shared/Skeleton";

/**
 * Security — what the scans found on each server, worst first. Always every
 * server, whichever project is selected: a server's OS, images and apps are
 * shared by every project on it, and its section lists those projects
 * (archived ones too: their servers are still scanned). Open findings show
 * one row per fix (security_units): the action that clears them, how many it
 * clears, and its button; a row expands to the findings. Severity
 * reuses the semantic status tokens (critical = error, high = warning, the
 * rest = queued; "no fix yet" = paused) — no new colors.
 */
export function SecurityView() {
  const [status, setStatus] = useState<"open" | "fixed">("open");

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["security", "summary"],
    queryFn: security.summary,
    refetchInterval: (q) =>
      q.state.data?.scanning_host || q.state.data?.patching_hosts.length ? 5_000 : 60_000,
  });
  const { data: findings, isLoading } = useQuery({
    queryKey: ["security", "findings", status],
    queryFn: () => security.findings({ status }),
    refetchInterval: 60_000,
  });
  const { data: units, isLoading: unitsLoading } = useQuery({
    queryKey: ["security", "units"],
    queryFn: () => security.units(),
    enabled: status === "open",
    refetchInterval: 60_000,
  });

  if (summaryLoading || isLoading || (status === "open" && unitsLoading)) {
    return (
      <div className="px-5 py-4 space-y-3">
        <Skeleton height={56} />
        <Skeleton height={32} />
        <Skeleton height={32} />
      </div>
    );
  }

  const hosts = summary?.hosts ?? [];
  const all = findings ?? [];

  return (
    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
      {summary && !summary.enabled && (
        <div className="text-small text-text-tertiary">
          Security scan is off (SECURITY_SCAN_ENABLED).
        </div>
      )}
      <Totals hosts={hosts} />
      <div className="flex items-center gap-3">
        <StatusSwitch active={status} onChange={setStatus} />
        <span className="text-mono text-[11px] text-text-tertiary">
          {all.length} {status}
        </span>
      </div>
      {hosts.length === 0 && (
        <div className="text-small text-text-tertiary">No servers yet: add a project.</div>
      )}
      {hosts.map((p) => (
        <HostSection
          key={p.host}
          p={p}
          scanning={summary?.scanning_host === p.host}
          patching={!!summary?.patching_hosts.includes(p.host)}
          findings={all.filter((f) => f.host === p.host)}
          units={(units ?? []).filter((u) => u.host === p.host)}
          status={status}
        />
      ))}
    </div>
  );
}

function severityColor(sev: VulnSeverity): { color: string; background: string } {
  switch (sev) {
    case "critical":
      return { color: "var(--status-error)", background: "var(--status-error-soft)" };
    case "high":
      return { color: "var(--status-warning)", background: "var(--status-warning-soft)" };
    default:
      return { color: "var(--status-queued)", background: "var(--status-queued-soft)" };
  }
}

function Totals({ hosts }: { hosts: SecurityHostSummary[] }) {
  const sum = (pick: (p: SecurityHostSummary) => number) =>
    hosts.reduce((acc, p) => acc + pick(p), 0);
  const tiles: { label: string; value: number; color?: string }[] = [
    { label: "CVEs", value: sum((p) => p.open_cves) },
    { label: "Critical", value: sum((p) => p.open_critical), color: "var(--status-error)" },
    { label: "High", value: sum((p) => p.open_high), color: "var(--status-warning)" },
    { label: "Medium", value: sum((p) => p.open_medium) },
    { label: "Low", value: sum((p) => p.open_low) },
    { label: "No fix yet", value: sum((p) => p.no_fix), color: "var(--status-paused)" },
  ];
  const exploited = sum((p) => p.exploited);
  if (exploited > 0) {
    tiles.unshift({ label: "Exploited", value: exploited, color: "var(--status-error)" });
  }
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-md bg-bg-inset px-3 py-2">
          <div className="text-caption text-text-tertiary uppercase tracking-wide">{t.label}</div>
          <div
            className="text-heading tabular-nums"
            style={{ color: t.value > 0 && t.color ? t.color : "var(--text-primary)" }}
          >
            {t.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusSwitch({
  active,
  onChange,
}: {
  active: "open" | "fixed";
  onChange: (v: "open" | "fixed") => void;
}) {
  const opts: { id: "open" | "fixed"; label: string }[] = [
    { id: "open", label: "Open" },
    { id: "fixed", label: "Fixed" },
  ];
  return (
    <div className="flex items-center gap-3" role="tablist" aria-label="Finding status">
      {opts.map((o) => {
        const on = o.id === active;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.id)}
            className={cn(
              "relative h-7 px-0.5 text-caption bg-transparent transition-colors duration-fast ease-out",
              on ? "text-text-primary" : "text-text-tertiary hover:text-text-primary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg-canvas",
            )}
          >
            {o.label}
            {on && (
              <span
                aria-hidden="true"
                className="absolute -bottom-1 left-0 right-0 h-[2px] bg-accent rounded-full"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function HostSection({
  p,
  scanning,
  patching,
  findings,
  units,
  status,
}: {
  p: SecurityHostSummary;
  scanning: boolean;
  patching: boolean;
  findings: VulnFinding[];
  units: FixUnit[];
  status: "open" | "fixed";
}) {
  const qc = useQueryClient();
  const scan = useMutation({
    mutationFn: () => security.scanNow(p.host),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["security"] });
      qc.invalidateQueries({ queryKey: ["loops", "runs"] });
    },
  });
  // Upgrades only the flagged OS packages, never reboots; the result and any
  // Reboot button go to the Telegram topic of the server's first project, then
  // the OS is rescanned.
  const patch = useMutation({
    mutationFn: () => security.patchNow(p.host),
    onSettled: () => qc.invalidateQueries({ queryKey: ["security"] }),
  });
  // Every OS package with a fix (any severity) plus one task per image or
  // lockfile with a fixable finding; Patch now stays the narrow, serious-only one.
  const fixAll = useMutation({
    mutationFn: () => security.fixAll(p.host),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["security"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
  const fixable = p.fixable_packages.length + p.fixable_places;
  const busy = scanning || patching;
  const scanned = p.last_scan_at
    ? `scanned ${relativeShort(p.last_scan_at)} ago`
    : "never scanned";
  const switchedOff = p.projects.every((proj) => !proj.security_scan_enabled);
  return (
    <section className="space-y-1">
      <div className="flex items-baseline gap-3 flex-wrap pb-1 border-b border-border-subtle">
        <span className="text-heading text-text-primary text-mono">{p.host}</span>
        <span className="text-small text-text-secondary">
          {p.projects.map((proj, i) => (
            <span key={proj.project_id}>
              {i > 0 && ", "}
              <span
                className={cn(!proj.active && "text-text-quaternary")}
                title={[
                  !proj.active && "archived",
                  !proj.security_scan_enabled && "repo not scanned (switch off in the Loops panel)",
                ]
                  .filter(Boolean)
                  .join("; ") || undefined}
              >
                {proj.name}
              </span>
            </span>
          ))}
        </span>
        <span className="text-mono text-[11px] text-text-tertiary">
          {patching ? "patching…" : scanning ? "scanning…" : scanned}
        </span>
        {switchedOff && (
          <span className="text-mono text-[11px] text-text-quaternary" title="Turn it on in the Loops panel">
            scan switch off
          </span>
        )}
        <span className="flex-1" />
        {status === "open" && fixable > 0 && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || fixAll.isPending}
            onClick={() => fixAll.mutate()}
            title={[
              p.fixable_packages.length > 0 &&
                `apt upgrade on ${p.host}: ${p.fixable_packages.join(", ")} (never reboots)`,
              p.fixable_places > 0 &&
                `a security task for each of ${p.fixable_places} image${p.fixable_places === 1 ? "" : "s"}/lockfile${p.fixable_places === 1 ? "" : "s"}`,
            ]
              .filter(Boolean)
              .join("; ")}
          >
            {`Fix all (${fixable})`}
          </Button>
        )}
        {status === "open" && (p.patchable.length > 0 || patching) && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || patch.isPending}
            onClick={() => patch.mutate()}
            title={`apt upgrade on ${p.host}: ${p.patchable.join(", ")}. Never reboots; then rescans.`}
          >
            {patching ? "Patching…" : `Patch now (${p.patchable.length})`}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          disabled={busy || scan.isPending}
          onClick={() => scan.mutate()}
          title="Run Trivy on this server now: OS, images, apps and every project repo on it"
        >
          {scanning ? "Scanning…" : "Scan now"}
        </Button>
      </div>
      {scan.isError && (
        <div className="text-mono text-[11px] text-[color:var(--status-error)]">
          {(scan.error as Error).message}
        </div>
      )}
      {patch.isError && (
        <div className="text-mono text-[11px] text-[color:var(--status-error)]">
          {(patch.error as Error).message}
        </div>
      )}
      {fixAll.isError && (
        <div className="text-mono text-[11px] text-[color:var(--status-error)]">
          {(fixAll.error as Error).message}
        </div>
      )}
      {fixAll.isSuccess && (
        <div className="text-mono text-[11px] text-text-tertiary">
          {fixAll.data.message}
          {fixAll.data.patch ? "; the patch result goes to Telegram." : "."}
        </div>
      )}
      {patch.isSuccess && (
        <div className="text-mono text-[11px] text-text-tertiary">
          {patch.data.message}; the result goes to Telegram.
        </div>
      )}
      {p.last_error && (
        <div
          className="text-mono text-[11px] text-[color:var(--status-error)] truncate"
          title={p.last_error}
        >
          {p.last_error}
        </div>
      )}
      {(status === "open" ? units.length : findings.length) === 0 && (
        <div className="text-small text-text-tertiary py-2">
          {status === "open"
            ? p.last_scan_at
              ? "No open findings."
              : "Nothing scanned yet."
            : "Nothing fixed yet."}
        </div>
      )}
      {status === "open"
        ? units.map((u) => <UnitRow key={u.key} u={u} busy={busy} />)
        : findings.map((f) => <FindingRow key={f.id} f={f} />)}
    </section>
  );
}

const UNIT_ACTION: Record<FixUnit["kind"], string | null> = {
  apt: "Upgrade",
  kernel: "Remove",
  pull: "Pull again",
  task: "Make task",
  manual: null,
  wait: null,
};

/** One fix: what clears these findings, how many, and the button that does it. */
function UnitRow({ u, busy }: { u: FixUnit; busy: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // Remove old kernel and Pull again take away a fallback: the first click
  // only arms the button, the second runs it.
  const [armed, setArmed] = useState(false);
  const selectTask = useUI((s) => s.setSelectedTaskId);
  const showActivity = useUI((s) => s.setRightPaneMode);
  const run = useMutation({
    mutationFn: () => security.runUnit(u.host, u.key, u.confirm),
    onSettled: () => {
      setArmed(false);
      qc.invalidateQueries({ queryKey: ["security"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
  const action = UNIT_ACTION[u.kind];
  const onClick = () => {
    if (u.confirm && !armed) setArmed(true);
    else run.mutate();
  };
  const sev = u.critical
    ? severityColor("critical")
    : u.high
      ? severityColor("high")
      : u.kind === "wait"
        ? { color: "var(--status-paused)", background: "var(--status-paused-soft)" }
        : severityColor("medium");
  const list = u.packages.length ? u.packages : u.targets;
  const more = u.findings - u.sample.length;
  return (
    <div className="border-b border-border-subtle">
      <div className="flex items-start gap-3 py-2 density-row">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Hide the findings" : "Show the findings"}
          className={cn(
            "shrink-0 mt-1 text-text-tertiary hover:text-text-primary transition-transform duration-fast",
            open && "rotate-90",
          )}
        >
          <ChevronRight size={14} />
        </button>
        <span
          className="text-mono text-[10px] px-1 h-[16px] inline-flex items-center rounded-sm shrink-0 mt-0.5 w-[58px] justify-center tabular-nums"
          style={sev}
          title={`${u.findings} findings, ${u.cves} distinct CVEs`}
        >
          {u.findings}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-body text-text-primary">{u.title}</span>
            <span className="text-mono text-[11px] text-text-tertiary">
              {u.cves} CVE{u.cves === 1 ? "" : "s"}
              {u.critical > 0 && ` · ${u.critical} critical`}
              {u.high > 0 && ` · ${u.high} high`}
            </span>
            {u.exploited > 0 && (
              <span
                className="text-mono text-[10px] px-1 h-[16px] inline-flex items-center rounded-sm"
                style={{ color: "var(--status-error)", background: "var(--status-error-soft)" }}
                title="On CISA's Known Exploited Vulnerabilities list"
              >
                exploited
              </span>
            )}
          </div>
          <div className="text-small text-text-secondary">{u.detail}</div>
          {list.length > 0 && (
            <div className="text-mono text-[11px] text-text-tertiary truncate" title={list.join(", ")}>
              {list.join(", ")}
            </div>
          )}
          {run.isError && (
            <div className="text-mono text-[11px] text-[color:var(--status-error)]">
              {(run.error as Error).message}
            </div>
          )}
          {run.isSuccess && (
            <div className="text-mono text-[11px] text-text-tertiary">
              {run.data.message}
              {run.data.outcome === "started" ? "; the result goes to Telegram." : ""}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          {u.task_id ? (
            <button
              type="button"
              onClick={() => {
                selectTask(u.task_id!);
                showActivity("activity");
              }}
              className="text-mono text-[11px] text-text-secondary hover:text-text-primary underline-offset-2 hover:underline"
              title="Open the task working on this"
            >
              on the board
            </button>
          ) : (
            action && (
              <Button
                variant={armed ? "primary" : "secondary"}
                size="sm"
                disabled={(u.kind !== "task" && busy) || run.isPending}
                onClick={onClick}
                onBlur={() => setArmed(false)}
                title={u.detail}
              >
                {armed ? `Sure? ${action}` : action}
              </Button>
            )
          )}
        </div>
      </div>
      {open && (
        <div className="pl-[90px]">
          {u.sample.map((f) => (
            <FindingRow key={f.id} f={f} inUnit />
          ))}
          {more > 0 && (
            <div className="text-mono text-[11px] text-text-tertiary py-2">
              +{more} more, all cleared by the same fix
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** One finding. Inside a fix row (``inUnit``) the row's own button is the action. */
function FindingRow({ f, inUnit = false }: { f: VulnFinding; inUnit?: boolean }) {
  const qc = useQueryClient();
  const selectTask = useUI((s) => s.setSelectedTaskId);
  const showActivity = useUI((s) => s.setRightPaneMode);
  const openTask = (id: string) => {
    selectTask(id);
    showActivity("activity");
  };
  // One card for every open finding in this image or lockfile; the worker
  // loop, gates and merge queue take it from there. OS packages use Patch now.
  const makeTask = useMutation({
    mutationFn: () => security.makeTask(f.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["security", "findings"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
  const sev = severityColor(f.severity);
  const where = f.surface === "os" ? "os" : `${f.surface} · ${f.target}`;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-border-subtle density-row">
      <span
        className="text-mono text-[10px] px-1 h-[16px] inline-flex items-center rounded-sm shrink-0 mt-0.5 w-[58px] justify-center"
        style={sev}
      >
        {f.severity}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-body text-text-primary">{f.package}</span>
          <span className="text-mono text-text-tertiary">{f.installed_version}</span>
          {f.fixed_version ? (
            <span className="text-mono text-text-secondary">→ {f.fixed_version}</span>
          ) : (
            <span
              className="text-mono text-[10px] px-1 h-[16px] inline-flex items-center rounded-sm"
              style={{ color: "var(--status-paused)", background: "var(--status-paused-soft)" }}
            >
              no fix yet
            </span>
          )}
          {f.exploited && (
            <span
              className="text-mono text-[10px] px-1 h-[16px] inline-flex items-center rounded-sm"
              style={{ color: "var(--status-error)", background: "var(--status-error-soft)" }}
              title="On CISA's Known Exploited Vulnerabilities list"
            >
              exploited
            </span>
          )}
        </div>
        {f.title && (
          <div className="text-small text-text-secondary truncate" title={f.title}>
            {f.title}
          </div>
        )}
        <div className="text-mono text-[11px] text-text-tertiary truncate" title={f.target}>
          {where}
        </div>
      </div>
      <div className="shrink-0 text-right">
        {f.url ? (
          <a
            href={f.url}
            target="_blank"
            rel="noreferrer"
            className="text-mono text-text-secondary hover:text-text-primary underline-offset-2 hover:underline"
          >
            {f.vuln_id}
          </a>
        ) : (
          <span className="text-mono text-text-secondary">{f.vuln_id}</span>
        )}
        {(f.task_id ||
          (!inUnit && f.status === "open" && f.surface !== "os" && f.surface !== "app")) && (
          <div className="mt-1">
            {f.task_id ? (
              <button
                type="button"
                onClick={() => openTask(f.task_id!)}
                className="text-mono text-[11px] text-text-secondary hover:text-text-primary underline-offset-2 hover:underline"
                title="Open the task this finding is on"
              >
                {f.status === "open" ? "on the board" : "task"}
              </button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={makeTask.isPending}
                onClick={() => makeTask.mutate()}
                title={`Add a security task to update ${f.target}`}
              >
                Make task
              </Button>
            )}
            {makeTask.isError && (
              <div className="text-mono text-[11px] text-[color:var(--status-error)]">
                {(makeTask.error as Error).message}
              </div>
            )}
          </div>
        )}
        <div className="text-mono text-[11px] text-text-tertiary">
          {f.status === "fixed" && f.resolved_at
            ? `fixed ${relativeShort(f.resolved_at)} ago`
            : `seen ${relativeShort(f.last_seen)} ago`}
        </div>
      </div>
    </div>
  );
}
